import React, {
  createContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Map from './map/map';
import * as Styled from './locationFinderStyle';
import { useLoadScript } from '@react-google-maps/api';
import useLocations from '../../hooks/useLocations';
import { locationTypes } from '../../utils/map';
import LocationList from './LocationList';
import LocationDetails from './locationDetails/LocationDetails';
import LocationBar from './LocationBar';
import LocationTypeFilter from './LocationTypeFilter';
import { useTranslation } from 'react-i18next';

export const LocationContext = createContext();
export const FilterContext = createContext();
export const UserContext = createContext();
const libraries = ['places'];
export default function LocationFinder({ heightOffsetRef }) {
  const { i18n } = useTranslation();

  const mapRef = useRef(null);
  const filterRef = useRef(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (
      !heightOffsetRef?.current?.clientHeight &&
      !filterRef?.current?.clientHeight
    ) {
      return 0;
    }
    if (window.innerWidth < 768) {
      return 0;
    }

    setHeight(
      heightOffsetRef?.current?.clientHeight + filterRef?.current?.clientHeight
    );
  }, [
    heightOffsetRef?.current?.clientHeight,
    filterRef?.current?.clientHeight,
  ]);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.GATSBY_GOOGLE_MAP_APIKEY2,
    libraries,
    language: i18n.resolvedLanguage === 'en' ? 'en' : 'fr-CA',
  });

  const [activeLocation, setActiveLocation] = useState();
  const [activeFilterIds, setActiveFilterIds] = useState([]);
  const [activeFilterProductIds, setActiveFilterProductIds] = useState([]);

  const [activeTypeId, setActiveTypeId] = useState(locationTypes[0].id);
  const useLocationValues = useMemo(() => {
    return {
      mapRef,
      activeTypeId,
      activeFilterIds,
      activeFilterProductIds,
    };
  }, [mapRef, activeTypeId, activeFilterIds, activeFilterProductIds]);

  const { locations, userPosition, handleUpdateUserPosition } =
    useLocations(useLocationValues);
  const updateMap = () => {
    mapRef.current?.setZoom(mapRef.current.getZoom());
  };

  const handleFilterChange = (id) => {
    // check if has filter if so remove otherwise add filter
    setActiveFilterIds((prev) =>
      prev.includes(id) ? prev.filter((prevId) => prevId !== id) : [...prev, id]
    );
    updateMap();
  };
  const handleProductFilterChange = (id) => {
    // check if has filter if so remove otherwise add filter
    setActiveFilterProductIds((prev) =>
      prev.includes(id) ? prev.filter((prevId) => prevId !== id) : [...prev, id]
    );
    updateMap();
  };

  const handleSetActiveLocation = (location) => {
    setActiveLocation(location);
    if (!location) {
      return;
    }
    mapRef.current?.panTo(location?.position);
    const currentZoomLevel = mapRef.current?.getZoom();
    if (currentZoomLevel < 10) {
      mapRef.current?.setZoom(14);
    }
  };

  const handleSetTypeFilterId = (typeId) => {
    setActiveTypeId(typeId);
    setActiveFilterIds([]);
    setActiveLocation(null);
    updateMap();
  };

  if (!isLoaded) {
    return <div>Loading</div>;
  }

  return (
    <LocationContext.Provider
      value={{
        locations,
        activeLocation,
        handleSetActiveLocation,
        handleSetTypeFilterId,
        activeFilterIds,
        activeFilterProductIds,
        activeTypeId,
        handleFilterChange,
        handleProductFilterChange,
      }}>
      <UserContext.Provider value={{ userPosition, handleUpdateUserPosition }}>
        <FilterContext.Provider
          value={{
            activeTypeId,
            setActiveTypeId,
            activeFilterIds,
            activeFilterProductIds,
            handleSetTypeFilterId,
            handleFilterChange,
            handleProductFilterChange,
          }}>
          <section className="tw-h-full tw-relative tw-flex tw-flex-col-reverse md:tw-flex-row">
            {loadError && (
              <div>Sorry, There was an error when trying to load the map</div>
            )}
            <div className="tw-flex tw-flex-col tw-min-h-[600px] md:tw-w-[400px] xl:tw-w-[575px] tw-static md:tw-relative">
              {activeLocation && <LocationDetails />}
              <div className="tw-bg-white" ref={filterRef}>
                <LocationBar />
                <LocationTypeFilter />
              </div>
              <div
                style={{ height: `calc(100vh - ${height}px)` }}
                className=" tw-overflow-y-auto tw-min-h-screen md:tw-min-h-[500px] md:tw-max-h-[1000px] tw-bg-red-300 tw-flex-grow">
                <LocationList className=" tw-flex-grow" locations={locations} />
              </div>
            </div>
            <div className="md:tw-flex-1 tw-h-auto">
              {!!isLoaded && (
                <Map
                  locations={locations}
                  mapRef={mapRef}
                  userPosition={userPosition}
                />
              )}
            </div>
          </section>
        </FilterContext.Provider>
      </UserContext.Provider>
    </LocationContext.Provider>
  );
}
